'use client';

import React from 'react';
import { useState, useEffect, useRef, FormEvent } from 'react';
import ExitIntentPopup from '@/components/ExitIntentPopup';
import { trackLead } from '@/lib/tracking';

// SVG Icons
const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const LoadingSpinner = () => (
  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
    </circle>
  </svg>
);

// Benefit icons
const CartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/>
    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
  </svg>
);

const NoFeesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="2" x2="22" y1="2" y2="22"/>
    <path d="M11.5 6.5c.6-.3 1.5-.5 2.5-.5 2.2 0 4 1.3 4 3s-1.8 3-4 3c-2.2 0-4 1.3-4 3s1.8 3 4 3c1 0 1.9-.2 2.5-.5"/>
    <path d="M12 2v2m0 16v2"/>
  </svg>
);

const DashboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/>
    <rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/>
  </svg>
);

const RocketIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
  </svg>
);

// CMS Feature icons
const ProductIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
    <path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>
  </svg>
);

const OrderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8Z"/>
    <path d="M15 3v4a1 1 0 0 0 1 1h4"/>
    <path d="m8 13 2.5 2L14 11"/>
  </svg>
);

const PaymentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>
  </svg>
);

const ShippingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
    <path d="M15 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.684-.948V8h4a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-1"/>
    <circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>
  </svg>
);

const AnalyticsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/>
  </svg>
);

const SeoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
    <path d="m11 8v6"/><path d="m8 11h6"/>
  </svg>
);

// Animated counter component
function CountUpNumber({ value }: { value: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [displayed, setDisplayed] = useState(value);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const match = value.match(/^(\d+)(.*)$/);
    if (!match) return;

    const target = parseInt(match[1], 10);
    const suffix = match[2];

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 1800;
          const start = performance.now();

          const animate = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.round(eased * target);
            setDisplayed(`${current}${suffix}`);
            if (progress < 1) requestAnimationFrame(animate);
          };

          setDisplayed(`0${suffix}`);
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return <p ref={ref} className="why-number">{displayed}</p>;
}

// FAQ Accordion
function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <button
        className={`faq-question ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        aria-expanded={isOpen}
      >
        <h3>{question}</h3>
        <svg
          className="faq-icon"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
      <div className={`faq-answer ${isOpen ? 'open' : ''}`} role="region">
        <p>{answer}</p>
      </div>
    </div>
  );
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
  consent?: string;
}

interface EshopyNaMiruContentProps {
  trackingConfig: {
    googleAdsId: string | null;
    googleAdsConversionLabel: string | null;
  };
  faqItems: { question: string; answer: string }[];
}

export default function EshopyNaMiruContent({ trackingConfig, faqItems }: EshopyNaMiruContentProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    budget: '',
    message: '',
    consent: false,
  });
  const [honeypot, setHoneypot] = useState('');
  const [formLoadedAt] = useState(Date.now());
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [budgetDropdownOpen, setBudgetDropdownOpen] = useState(false);
  const budgetDropdownRef = useRef<HTMLDivElement>(null);

  const budgetOptions = [
    { value: '', label: 'Vyberte rozpočet' },
    { value: 'do 50 000 Kč', label: 'do 50 000 Kč' },
    { value: '50 000 - 100 000 Kč', label: '50 000 - 100 000 Kč' },
    { value: '100 000 - 200 000 Kč', label: '100 000 - 200 000 Kč' },
    { value: 'nad 200 000 Kč', label: 'nad 200 000 Kč' },
  ];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (budgetDropdownRef.current && !budgetDropdownRef.current.contains(event.target as Node)) {
        setBudgetDropdownOpen(false);
      }
    }

    if (budgetDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [budgetDropdownOpen]);

  const validateField = (name: string, value: string | boolean): string | undefined => {
    switch (name) {
      case 'name':
        if (!value || (typeof value === 'string' && value.trim().length < 2)) {
          return 'Jméno musí mít alespoň 2 znaky';
        }
        break;
      case 'email':
        if (!value || (typeof value === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))) {
          return 'Zadejte platný e-mail';
        }
        break;
      case 'message':
        if (!value || (typeof value === 'string' && value.trim().length < 10)) {
          return 'Zpráva musí mít alespoň 10 znaků';
        }
        break;
      case 'consent':
        if (!value) {
          return 'Musíte souhlasit se zpracováním údajů';
        }
        break;
    }
    return undefined;
  };

  const handleBlur = (name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof typeof formData]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleChange = (name: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const newErrors: FormErrors = {};
    (['name', 'email', 'message', 'consent'] as const).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key as keyof FormErrors] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({ name: true, email: true, message: true, consent: true });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          budget: formData.budget || undefined,
          message: (formData.website ? `[Web: ${formData.website}]\n` : '') + formData.message,
          source: 'eshop-landing',
          website: honeypot,
          _t: formLoadedAt,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', website: '', budget: '', message: '', consent: false });
        setTouched({});
        setErrors({});

        // Facebook Pixel Lead tracking
        trackLead({
          source: 'landing-eshopy',
          name: formData.name,
          email: formData.email,
          budget: formData.budget || undefined,
        });

        if (typeof window !== 'undefined' && typeof window.gtag === 'function' && trackingConfig.googleAdsId && trackingConfig.googleAdsConversionLabel) {
          window.gtag('event', 'conversion', {
            send_to: `${trackingConfig.googleAdsId}/${trackingConfig.googleAdsConversionLabel}`,
            value: 1.0,
            currency: 'CZK',
          });
        }
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  const scrollToForm = () => {
    document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
  };

  const benefits = [
    {
      icon: <NoFeesIcon />,
      title: 'Žádné měsíční poplatky',
      description: 'E-shop je váš. Neplatíte pronájem platformy, neplatíte za pluginy. Peníze investujete do růstu.',
    },
    {
      icon: <DashboardIcon />,
      title: 'Plná kontrola nad funkcemi',
      description: 'Potřebujete něco navíc? Žádný problém. Žádné limity šablony, žádné čekání na podporu.',
    },
    {
      icon: <CartIcon />,
      title: 'Rychlejší nákupní proces',
      description: 'Od produktu k platbě za pár kliknutí. Méně kroků = víc dokončených objednávek.',
    },
    {
      icon: <RocketIcon />,
      title: 'Připravený na růst',
      description: 'Napojení na platby, dopravu, fakturaci i marketing bez omezení. E-shop roste s vaším byznysem.',
    },
  ];

  const cmsFeatures = [
    {
      icon: <ProductIcon />,
      title: 'Správa produktů',
      description: 'Varianty, fotogalerie, kategorie, filtry, skladové zásoby. Přidáte produkt za minutu.',
    },
    {
      icon: <OrderIcon />,
      title: 'Objednávky pod kontrolou',
      description: 'Přehled stavů, automatické e-maily zákazníkům, export dat. Nic vám neuteče.',
    },
    {
      icon: <PaymentIcon />,
      title: 'Platby bez komplikací',
      description: 'GoPay, Comgate, Stripe — kartou, převodem, Apple Pay i Google Pay. Zákazník zaplatí, jak mu vyhovuje.',
    },
    {
      icon: <ShippingIcon />,
      title: 'Doprava vyřešená',
      description: 'Zásilkovna, PPL, DPD, Česká pošta. Cena dopravy se spočítá automaticky.',
    },
    {
      icon: <AnalyticsIcon />,
      title: 'Data, která pomáhají prodávat',
      description: 'Tržby, nejprodávanější produkty, konverzní poměr. Víte přesně, co funguje.',
    },
    {
      icon: <SeoIcon />,
      title: 'SEO přímo v administraci',
      description: 'Meta tagy, URL adresy, sitemap, strukturovaná data. Lepší viditelnost na Googlu bez externích nástrojů.',
    },
  ];

  const processSteps = [
    { number: 1, title: 'Konzultace zdarma', description: 'Probereme sortiment, zákazníky a co od e-shopu očekáváte. Bez závazků.' },
    { number: 2, title: 'Návrh a odsouhlasení', description: 'Uvidíte design a strukturu dřív, než začnu programovat.' },
    { number: 3, title: 'Vývoj a napojení', description: 'Postavím e-shop, napojím platby, dopravu, fakturaci.' },
    { number: 4, title: 'Testování a spuštění', description: 'Otestujeme celý nákupní proces. Pak jedeme ostře.' },
  ];

  return (
    <>
      {/* Hero Section */}
      <header className="landing-hero eshop-hero">
        <div className="geo-shapes" aria-hidden="true">
          <svg className="geo-ring" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="90" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
            <circle cx="100" cy="100" r="60" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
          </svg>
          <svg className="geo-cross" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="50" y1="0" x2="50" y2="100" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
            <line x1="0" y1="50" x2="100" y2="50" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
          </svg>
          <svg className="geo-dots" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            {[0, 30, 60, 90].map(x => [0, 30, 60, 90].map(y => (
              <circle key={`${x}-${y}`} cx={x + 15} cy={y + 15} r="2" fill="rgba(255,255,255,0.2)" />
            )))}
          </svg>
          <div className="geo-line-diagonal"></div>
          <svg className="geo-ring-left" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="90" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
            <circle cx="100" cy="100" r="65" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
            <circle cx="100" cy="100" r="40" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
          </svg>
          <svg className="geo-triangle" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polygon points="50,5 95,95 5,95" stroke="rgba(255,255,255,0.18)" strokeWidth="1" fill="none" />
          </svg>
          <div className="geo-line-left"></div>
          <svg className="geo-dots-left" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            {[0, 25, 50, 75].map(x => [0, 25, 50, 75].map(y => (
              <circle key={`l-${x}-${y}`} cx={x + 12} cy={y + 12} r="1.5" fill="rgba(255,255,255,0.2)" />
            )))}
          </svg>
          <svg className="geo-diamond" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="29" y="29" width="42" height="42" stroke="rgba(255,255,255,0.15)" strokeWidth="1" fill="none" transform="rotate(45 50 50)" />
          </svg>
        </div>

        <div className="landing-hero-content">
          <div className="landing-hero-text header-animate">
            <h1 className="anim-item">
              Přestaňte platit za e-shop,<br />
              <span className="highlight">který nikdy nebude váš</span>
            </h1>
            <p className="landing-hero-subtitle anim-item">
              Vytvořím vám e-shop na míru bez měsíčních poplatků.<br />
              Plná kontrola, žádná omezení a řešení, které roste s vaším byznysem.
            </p>
            <div className="landing-hero-cta anim-item">
              <button onClick={scrollToForm} className="btn-white">
                Chci vlastní e-shop
              </button>
              <button onClick={() => document.getElementById('cenik')?.scrollIntoView({ behavior: 'smooth' })} className="btn-text">
                Kolik ušetřím?
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Social Proof - Stats */}
      <div className="why why-landing" id="why">
        <div className="why-content">
          <div className="why-box">
            <div className="info">
              <CountUpNumber value="12+" />
              <p>let na trhu</p>
            </div>
          </div>
          <div className="why-box">
            <div className="info">
              <CountUpNumber value="50+" />
              <p>spuštěných e-shopů</p>
            </div>
          </div>
          <div className="why-box">
            <div className="info">
              <CountUpNumber value="0 Kč" />
              <p>měsíčně za platformu</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pain Points */}
      <section className="why-new-web">
        <div className="why-new-web-content">
          <div className="why-new-web-header">
            <span className="section-label">Poznáváte se?</span>
            <h2>Pronajatý e-shop vás brzdí</h2>
            <p>Platíte, přizpůsobujete se a nemáte kontrolu. Zní povědomě?</p>
          </div>
          <ul className="pain-points">
            <li>
              <span className="pain-icon" aria-hidden="true">✕</span>
              Každý měsíc platíte další a další poplatky
            </li>
            <li>
              <span className="pain-icon" aria-hidden="true">✕</span>
              Nemůžete upravit, co potřebujete
            </li>
            <li>
              <span className="pain-icon" aria-hidden="true">✕</span>
              Jste závislí na platformě a jejích podmínkách
            </li>
            <li>
              <span className="pain-icon" aria-hidden="true">✕</span>
              Při růstu narážíte na limity
            </li>
          </ul>
          <button onClick={scrollToForm} className="btn-primary-large">Chci vlastní e-shop</button>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits">
        <div className="benefits-content">
          <div className="benefits-header">
            <span className="section-label">Proč e-shop na míru</span>
            <h2>Co vám krabicové řešení nikdy nedá</h2>
            <p>Shoptet a Shopify vás rozjedou. Ale když chcete růst bez limitů, potřebujete víc.</p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-box">
                <div className="benefit-icon" aria-hidden="true">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
          <div className="benefits-cta">
            <button onClick={scrollToForm} className="btn-primary-large">
              Chci vlastní e-shop
            </button>
          </div>
        </div>
      </section>

      {/* CMS Demo Section */}
      <section className="cms-demo" id="cms-demo">
        <div className="cms-demo-content">
          <div className="cms-demo-header">
            <span className="section-label">Co je součástí</span>
            <h2>Všechno, co e-shop potřebuje</h2>
            <p>150+ funkcí v ceně. Žádné placené pluginy, žádné doplňky za příplatek.</p>
          </div>
          <div className="cms-features-grid">
            {cmsFeatures.map((feature, index) => (
              <div key={index} className="cms-feature-card">
                <div className="cms-feature-icon" aria-hidden="true">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategy Quote */}
      <section className="strategy-quote">
        <div className="strategy-quote-content">
          <blockquote>
            <p>
              Váš e-shop není katalog — je to prodejce, který pracuje 24 hodin denně. Přehledná navigace, rychlé načítání, jednoduchý checkout. Každý detail má jediný cíl: aby zákazník dokončil objednávku, ne aby odešel ke konkurenci.
            </p>
          </blockquote>
        </div>
      </section>

      {/* Process */}
      <section className="process">
        <div className="process-content">
          <div className="process-header">
            <span className="process-label">Jak spolupracujeme</span>
            <h2>4 kroky ke spuštění e-shopu</h2>
            <p>Jasný postup, ve kterém víte co se děje a kdy začnete prodávat.</p>
          </div>
          <div className="process-grid">
            {processSteps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="process-step">
                  <div className="step-top">
                    <span className="step-label">Krok {String(step.number).padStart(2, '0')}</span>
                    <h3>{step.title}</h3>
                  </div>
                  <p>{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="process-arrow" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="cost-compare" id="cenik">
        <div className="cost-compare-inner">
          <div className="cost-compare-header">
            <span className="section-label">Srovnání nákladů</span>
            <h2>Spočítejte si, kolik vás pronájem stojí</h2>
            <p>Za pár let zaplatíte za pronájem desítky tisíc — a e-shop vám stejně nikdy nepatří.</p>
          </div>

          {/* 3-year totals */}
          <div className="cost-totals">
            <div className="cost-totals-item bad">
              <span className="cost-totals-label">Pronajatá platforma za 3 roky</span>
              <span className="cost-totals-price">83 700 Kč</span>
              <span className="cost-totals-note">a pořád nic nevlastníte</span>
            </div>
            <div className="cost-totals-vs">vs.</div>
            <div className="cost-totals-item good">
              <span className="cost-totals-label">Vlastní e-shop za 3 roky</span>
              <span className="cost-totals-price">60 700 Kč</span>
              <span className="cost-totals-note">a e-shop je navždy váš</span>
            </div>
          </div>

          {/* Savings callout */}
          <div className="cost-savings">
            <span className="cost-savings-amount">Ušetříte 23 000 Kč</span>
            <span className="cost-savings-sub">za 3 roky. A čím déle e-shop provozujete, tím víc ušetříte.</span>
          </div>

          {/* Detailed breakdown */}
          <div className="cost-details">
            <div className="cost-details-col">
              <h3>Na co vyhazujete u platformy</h3>
              <ul>
                <li>Měsíční poplatek za platformu <strong>~700 Kč</strong></li>
                <li>Plugin dopravce (Zásilkovna) <strong>~500 Kč</strong></li>
                <li>Plugin dopravce (Balíkovna/PPL) <strong>~500 Kč</strong></li>
                <li>Plugin fakturace (Fakturoid) <strong>~375 Kč</strong></li>
                <li>Plugin newsletter <strong>~250 Kč</strong></li>
              </ul>
              <div className="cost-details-sum bad">Celkem ~2 325 Kč / měsíc</div>
              <p className="cost-details-extra">
                + design z šablony jako stovky dalších e-shopů. Chcete změnu? Zaplaťte kodéra. A kód vám stejně nikdy nebude patřit.
              </p>
            </div>
            <div className="cost-details-col highlight">
              <h3>Co platíte s vlastním e-shopem</h3>
              <ul>
                <li>Server (hosting) <strong>~300 Kč</strong></li>
                <li>Doména <strong>~17 Kč</strong></li>
                <li>Dopravci, platby, fakturace, feedy <strong>0 Kč</strong></li>
                <li>Poplatek za platformu <strong>0 Kč</strong></li>
                <li>Pluginy a doplňky <strong>0 Kč</strong></li>
              </ul>
              <div className="cost-details-sum good">Celkem ~317 Kč / měsíc</div>
              <p className="cost-details-extra">
                + jednorázově od 49 900 Kč. Design 100 % na míru, 150+ funkcí v ceně. E-shop je váš navždy. Investice se vrátí za ~25 měsíců.
              </p>
            </div>
          </div>

          <div className="cost-compare-cta">
            <button onClick={scrollToForm} className="btn-primary-large">
              Chci vlastní e-shop
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq">
        <div className="faq-content">
          <div className="faq-header">
            <span className="faq-label">Máte dotazy?</span>
            <h2>Často kladené otázky</h2>
          </div>
          <div className="faq-list">
            {faqItems.map((item, index) => (
              <FaqItem key={index} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Inline Contact Form */}
      <section className="inline-form" id="kontakt">
        <div className="inline-form-content">
          <div className="inline-form-header">
            <span className="form-label">Začněte šetřit</span>
            <h2>Kolik vás stojí váš e-shop každý měsíc navíc?</h2>
            <p>Napište mi a do 24 hodin se ozvu. Konzultace je zdarma a bez závazků.</p>
          </div>

          {submitStatus === 'success' ? (
            <div className="form-success">
              <div className="success-check" aria-hidden="true">
                <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="26" cy="26" r="25" stroke="#fff" strokeWidth="2"/>
                  <path d="M15 27l7 7 15-15" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Děkuji za zprávu!</h3>
              <p>Ozvu se Vám co nejdříve, obvykle do 24 hodin.</p>
              <button className="btn-primary-large" onClick={() => setSubmitStatus('idle')}>
                Odeslat další zprávu
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="landing-form" noValidate>
              <input type="text" name="company_url" value={honeypot} onChange={e => setHoneypot(e.target.value)} style={{ position: 'absolute', left: '-9999px', tabIndex: -1 } as React.CSSProperties} autoComplete="off" aria-hidden="true" />
              <div className="form-row">
                <div className={`form-group ${errors.name && touched.name ? 'has-error' : ''}`}>
                  <label htmlFor="eshop-name">Jméno a příjmení *</label>
                  <input
                    type="text"
                    id="eshop-name"
                    name="name"
                    autoComplete="name"
                    placeholder="Jan Novák"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    onBlur={() => handleBlur('name')}
                  />
                  {errors.name && touched.name && (
                    <span className="field-error" role="alert">{errors.name}</span>
                  )}
                </div>

                <div className={`form-group ${errors.email && touched.email ? 'has-error' : ''}`}>
                  <label htmlFor="eshop-email">E-mail *</label>
                  <input
                    type="email"
                    id="eshop-email"
                    name="email"
                    autoComplete="email"
                    placeholder="jan@example.cz"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    onBlur={() => handleBlur('email')}
                  />
                  {errors.email && touched.email && (
                    <span className="field-error" role="alert">{errors.email}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eshop-phone">Telefon <span className="optional">(nepovinné)</span></label>
                  <input
                    type="tel"
                    id="eshop-phone"
                    name="phone"
                    autoComplete="tel"
                    placeholder="+420 777 888 999"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label id="eshop-budget-label">Rozpočet <span className="optional">(nepovinné)</span></label>
                  <div
                    ref={budgetDropdownRef}
                    className={`custom-select ${budgetDropdownOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="custom-select-trigger"
                      onClick={() => setBudgetDropdownOpen(!budgetDropdownOpen)}
                      aria-labelledby="eshop-budget-label"
                      aria-expanded={budgetDropdownOpen}
                      aria-haspopup="listbox"
                    >
                      <span>{formData.budget || 'Vyberte rozpočet'}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                    </button>
                    {budgetDropdownOpen && (
                      <div className="custom-select-options" role="listbox" aria-labelledby="eshop-budget-label">
                        {budgetOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={formData.budget === option.value}
                            className={`custom-select-option ${formData.budget === option.value ? 'selected' : ''}`}
                            onClick={() => {
                              handleChange('budget', option.value);
                              setBudgetDropdownOpen(false);
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="eshop-website">Adresa stávajícího webu <span className="optional">(nepovinné)</span></label>
                <input
                  type="url"
                  id="eshop-website"
                  name="website"
                  autoComplete="url"
                  placeholder="https://vaseshop.cz"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                />
              </div>

              <div className={`form-group ${errors.message && touched.message ? 'has-error' : ''}`}>
                <label htmlFor="eshop-message">Zpráva *</label>
                <textarea
                  id="eshop-message"
                  name="message"
                  placeholder="Popište svůj e-shop — co prodáváte, kolik máte produktů, jaké máte představy..."
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  onBlur={() => handleBlur('message')}
                  rows={5}
                />
                {errors.message && touched.message && (
                  <span className="field-error" role="alert">{errors.message}</span>
                )}
              </div>

              <div className={`form-check ${errors.consent && touched.consent ? 'has-error' : ''}`}>
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={formData.consent}
                    onChange={(e) => handleChange('consent', e.target.checked)}
                    onBlur={() => handleBlur('consent')}
                  />
                  <span className="checkbox-mark"></span>
                  <span className="checkbox-text">
                    Souhlasím se zpracováním osobních údajů *
                  </span>
                </label>
                {errors.consent && touched.consent && (
                  <span className="field-error" role="alert">{errors.consent}</span>
                )}
              </div>

              <button className="btn-submit-large" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <LoadingSpinner />
                    <span>Odesílám...</span>
                  </>
                ) : (
                  <span>Odeslat poptávku</span>
                )}
              </button>

              {submitStatus === 'error' && (
                <div className="form-error" role="alert">
                  <p>Něco se pokazilo. Zkuste to prosím znovu.</p>
                </div>
              )}
            </form>
          )}
        </div>
      </section>

      {/* Exit Intent Popup */}
      <ExitIntentPopup onCtaClick={scrollToForm} />
    </>
  );
}
